/**
 * The C·R·A·I·D lifecycle, as the screen reads it.
 *
 * Two ratified sources already agreed about this and the build had guessed a
 * third answer, which is the whole reason this module exists:
 *
 *  - `LedgerFlows.cs` holds the per-type vocabularies and `IsTerminal`, which
 *    is what the API enforces when a transition is walked.
 *  - `LifecyclePill.jsx`'s `LIFECYCLES` holds the per-type LABEL and TONE, and
 *    `Work.dc.html`'s own fixture rows carry `{ lcState: 'Cleared', open: false }`
 *    beside them — the design stating terminality per record.
 *
 * **The tone IS the terminality.** `open` and `active` are exactly
 * `LedgerFlows.OpenStatuses`; `positive`, `neutral` and `negative` are exactly
 * the terminal set. So there is no separate closed-list to maintain here and
 * no second place for the two to drift apart — asking for the label answers
 * the other question by construction.
 *
 * What this replaces: a set reading
 * `closed · done · dismissed · resolved · promoted · accepted`, which is the
 * WORK-ITEM vocabulary (`done` and `dismissed` are ADR-0031 Amendment §1's
 * states; `closed` and `promoted` are task states). Only `resolved` and
 * `accepted` overlap the Ledger's twelve terminal statuses, and they overlap by
 * coincidence. Ten were missing, so a cleared concern, an averted or
 * materialized risk, a validated or invalidated assumption, a won't-fix issue
 * and a superseded, reversed or aged-out decision all rendered as live work.
 *
 * One name differs between the two sources and the difference is deliberate
 * (Erik, 2026-09-11): a concern closes `escalated` in the schema and reads
 * **Converted** on screen. `escalated` is the key of a forced conversion in
 * `LedgerFlows`, so renaming it would be a data change for a display problem —
 * the same disposition ADR-0031's Amendment gave `subject`, which keeps its
 * schema name and never appears in UI copy.
 *
 * The vocabulary is OPEN (C-019, governed in code, additive by PR, never a DB
 * enum), so an unrecognised status degrades rather than throwing: it is
 * humanised for display and treated as NOT terminal. Both halves are
 * deliberate. Humanising keeps `wont_fix` off the screen, which is the H1 rule
 * wave 16 made structural for event names and field keys. Treating it as live
 * keeps an unknown record visible instead of quietly filing it under closed,
 * because guessing terminality in the closed direction hides work.
 */

export type LifecycleTone = 'open' | 'active' | 'positive' | 'neutral' | 'negative' | 'unknown'

/** The tones that mean the record is still live. Everything else is a close. */
const LIVE_TONES: ReadonlySet<LifecycleTone> = new Set<LifecycleTone>(['open', 'active', 'unknown'])

type Vocabulary = Readonly<Record<string, { label: string; tone: LifecycleTone }>>

/**
 * Per-type, keyed by the API's schema value. Labels and tones are
 * `LIFECYCLES`' own, transcribed rather than re-derived.
 */
const LIFECYCLES: Readonly<Record<string, Vocabulary>> = {
  concern: {
    raised: { label: 'Raised', tone: 'open' },
    watching: { label: 'Watching', tone: 'active' },
    cleared: { label: 'Cleared', tone: 'positive' },
    // The one deliberate schema/display split — see the header.
    escalated: { label: 'Converted', tone: 'neutral' },
  },
  risk: {
    mitigating: { label: 'Mitigating', tone: 'active' },
    averted: { label: 'Averted', tone: 'positive' },
    accepted: { label: 'Accepted', tone: 'neutral' },
    materialized: { label: 'Materialized', tone: 'negative' },
  },
  assumption: {
    open: { label: 'Open', tone: 'open' },
    validated: { label: 'Validated', tone: 'positive' },
    invalidated: { label: 'Invalidated', tone: 'negative' },
  },
  issue: {
    resolving: { label: 'Resolving', tone: 'active' },
    resolved: { label: 'Resolved', tone: 'positive' },
    wont_fix: { label: "Won't-fix", tone: 'neutral' },
  },
  decision: {
    active: { label: 'Active', tone: 'active' },
    superseded: { label: 'Superseded', tone: 'neutral' },
    reversed: { label: 'Reversed', tone: 'negative' },
    aged_out: { label: 'Aged-out', tone: 'neutral' },
  },
}

/**
 * `wont_fix` → `Wont fix`. Only reached for a status this build does not know,
 * which means the app is behind the API's vocabulary rather than that something
 * is wrong. Says less rather than leaking, per `speakable()`'s posture.
 */
function humanise(status: string): string {
  const words = status.replace(/[_-]+/g, ' ').trim()
  if (words === '') return ''
  return words.charAt(0).toUpperCase() + words.slice(1).toLowerCase()
}

function lookup(type: string | null | undefined, status: string | null | undefined) {
  if (!type || !status) return null
  return LIFECYCLES[type.toLowerCase()]?.[status.toLowerCase()] ?? null
}

/** The governed label for a C·R·A·I·D standing, or a humanised fallback. Null when there is no standing at all. */
export function standingLabel(
  type: string | null | undefined,
  status: string | null | undefined,
): string | null {
  if (!status) return null
  return lookup(type, status)?.label ?? humanise(status) ?? null
}

/** The pill's tone. `unknown` for a status this build does not carry, which renders like `open`. */
export function standingTone(
  type: string | null | undefined,
  status: string | null | undefined,
): LifecycleTone | null {
  if (!status) return null
  return lookup(type, status)?.tone ?? 'unknown'
}

/**
 * Is this record closed?
 *
 * The API's `LedgerFlows.IsTerminal` is the authority and this agrees with it
 * by construction, because both read the same per-type vocabulary. An
 * unrecognised status is NOT terminal — see the header.
 */
export function isTerminalStanding(
  type: string | null | undefined,
  status: string | null | undefined,
): boolean {
  const tone = standingTone(type, status)
  return tone !== null && !LIVE_TONES.has(tone)
}

/** The twelve terminal statuses, for the harnesses to assert against rather than re-type. */
export const TERMINAL_STATUSES: ReadonlyArray<{ type: string; status: string; label: string }> =
  Object.entries(LIFECYCLES).flatMap(([type, vocabulary]) =>
    Object.entries(vocabulary)
      .filter(([, v]) => !LIVE_TONES.has(v.tone))
      .map(([status, v]) => ({ type, status, label: v.label })),
  )

/**
 * Risks with no mitigation on the plan — the UNMITIGATED tile's count.
 *
 * <b>Open risks only.</b> A closed risk is not unmitigated, it is done: an
 * averted risk needed no task and a materialized one became an issue. Counting
 * them would make the tile grow as the register is worked, which is backwards.
 *
 * The `mitigated_by` edge is RFC-0014 §3's remediation-to-plan link, governed in
 * the Ledger family's grammar and drawn by §5's own act — "JEDI offers to spin a
 * mitigation into a Task on confirmation". Until wave 18 it was emitted by one
 * fixture line and read by nothing, which is why this tile said it was absent
 * rather than rendering a number nothing stood behind.
 */
export function unmitigatedRisks(
  entries: ReadonlyArray<{ type?: string | null; lifecycleStatus?: string | null; links?: ReadonlyArray<{ linkType?: string | null }> | null }>,
): number {
  return entries.filter((e) =>
    (e.type ?? '').toLowerCase() === 'risk'
    && !isTerminalStanding(e.type, e.lifecycleStatus)
    && !(e.links ?? []).some((l) => (l.linkType ?? '').toLowerCase() === 'mitigated_by'),
  ).length
}
